import { isActiveGroup } from 'sonata-static-storage/lib/helpers/itemsGrouping.helpers';

import { COLOR_PICKER_COLORS } from '@/components/common/ColorPicker/colorPicker.constants';
import { supportsCartographyGrouping } from './cartographyGrouping';
import { createGroupId, toId } from './groupIds';
import { addItems } from './groupMembership';

import type { CartographyGroupProps } from 'sonata-static-storage/lib/schemas/campaign/screenGroup.schema';
import type {
  AdGroupRefs,
  CartographyAssignment,
  GroupableItem,
  GroupChange,
  GroupChangeContext,
  GroupChangeOutcome,
  GroupingState,
  GroupLike,
  TemporalPacingLike
} from './grouping.types';

type CreateCartographyChange = Extract<GroupChange, { type: 'createCartography' }>;

const isCartographyGroup = (group: GroupLike): boolean => isActiveGroup(group) && group.groupType === 'cartography';

const sameArea = (props: CartographyGroupProps | undefined, target: CartographyGroupProps): boolean =>
  props !== undefined &&
  props.areaCode === target.areaCode &&
  props.level === target.level &&
  props.country.toUpperCase() === target.country.toUpperCase();

/** Item ids per area code, in order of first appearance (items without area are skipped). */
function itemIdsByArea(assignments: CartographyAssignment[]): Map<string, string[]> {
  const byArea = new Map<string, string[]>();
  assignments.forEach(({ areaCode, itemId }) => {
    if (areaCode !== null) {
      byArea.set(areaCode, [...(byArea.get(areaCode) ?? []), itemId]);
    }
  });
  return byArea;
}

/**
 * Resolves a `createCartography` change: one cartographic group per area, reusing the active group of the same
 * country/level/area. New groups take the next palette colour, continuing after the existing cartographic groups.
 */
export function createCartographyGroups<
  G extends GroupLike,
  I extends GroupableItem,
  A extends AdGroupRefs,
  T extends TemporalPacingLike
>(
  state: GroupingState<G, I, A, T>,
  { areaNames, assignments, country, level }: CreateCartographyChange,
  context: GroupChangeContext<G>
): GroupChangeOutcome<G, I, A, T> {
  const noReferences = { ads: 0, temporalPacing: 0 };
  if (!supportsCartographyGrouping(context.family)) {
    const cartography = { createdGroupIds: [], excludedItemCount: 0, reusedGroupIds: [] };
    return { affectedItemIds: [], cartography, deletedGroups: [], references: noReferences, state };
  }

  const colorOffset = state.groups.filter(isCartographyGroup).length;
  const groups = [...state.groups];
  let items = state.items;
  const affected = new Set<string>();
  const createdGroupIds: string[] = [];
  const reusedGroupIds: string[] = [];

  itemIdsByArea(assignments).forEach((itemIds, areaCode) => {
    const props: CartographyGroupProps = { areaCode, country, level };
    const existing = state.groups.find(group => isCartographyGroup(group) && sameArea(group.cartographyProps, props));
    let groupId = toId(existing?._id);
    if (existing) {
      reusedGroupIds.push(groupId);
    } else {
      groupId = createGroupId();
      const color = COLOR_PICKER_COLORS[(colorOffset + createdGroupIds.length) % COLOR_PICKER_COLORS.length];
      const name = (areaNames[areaCode] ?? areaCode).trim();
      groups.push({
        ...context.buildGroup({ _id: groupId, color, name }),
        cartographyProps: props,
        groupType: 'cartography'
      });
      createdGroupIds.push(groupId);
    }
    const added = addItems({ items }, context.family, groupId, itemIds);
    items = added.items;
    added.affectedItemIds.forEach(id => {
      affected.add(id);
    });
  });

  return {
    affectedItemIds: [...affected],
    cartography: {
      createdGroupIds,
      excludedItemCount: assignments.filter(({ areaCode }) => areaCode === null).length,
      reusedGroupIds
    },
    deletedGroups: [],
    references: noReferences,
    state: { ...state, groups, items }
  };
}
